// CrystalVault — Main App Entry Point

// Register the same-origin service worker (see /sw.js). Cache-first for
// static assets means subsequent visits paint in <100ms; HTML stays
// network-first so deploys land on next refresh. Registration is no-op
// over HTTP / on browsers without SW support — fail-closed, never throws.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => {
      console.warn('SW registration failed:', e);
    });
  });
}

import {
  NETWORK,
  NETWORKS,
  getActiveNetworkKey,
  setActiveNetworkKey,
  getActiveNetwork,
} from './config.js';
import { connect, tryReconnect, disconnect, isConnected, getAddress, getUsdcBalance, shortAddr } from './wallet.js';
import { renderOverview, initOverview } from './pages/overview.js';
import { renderVaults, initVaults } from './pages/vaults.js';
import { renderDeposit, initDeposit } from './pages/deposit.js';
import { renderTrades, initTrades } from './pages/trades.js';
import { renderVaultDetail, initVaultDetail } from './pages/vault-detail.js';
import { renderAffiliate, initAffiliate } from './pages/affiliate.js';
import { initTxModal } from './tx.js';
import { wireHowItWorksTriggers } from './how-it-works.js';

// ── Router ────────────────────────────────────────────────────────────
const routes = {
  overview:  { render: renderOverview,  init: initOverview },
  vaults:    { render: renderVaults,    init: initVaults },
  deposit:   { render: renderDeposit,   init: initDeposit },
  trades:    { render: renderTrades,    init: initTrades },
  affiliate: { render: renderAffiliate, init: initAffiliate },
};

function getHash() {
  const raw = window.location.hash.replace('#', '').split('?')[0] || 'overview';
  return raw;
}

function updateNav(page) {
  document.querySelectorAll('[data-nav-link]').forEach(link => {
    const target = link.dataset.navLink;
    if (target === page) {
      link.className = "font-['Space_Grotesk'] tracking-wider uppercase text-xs text-[#22D3EE] border-b-2 border-[#22D3EE] pb-1 transition-colors";
    } else {
      link.className = "font-['Space_Grotesk'] tracking-wider uppercase text-xs text-[#89ceff] hover:text-[#22D3EE] transition-colors";
    }
  });
}

// Per-page max-width override for #page-content. Default for every page
// is `max-w-6xl` (1152px). Trades page bumps to `max-w-[1320px]` —
// just enough headroom for the new Exit Price column (~150px) without
// stretching the KPI tiles or filter row noticeably wider than before.
// Visual rhythm matches the original 8-column layout.
const WIDE_PAGES = new Set(['trades']);
function _applyPageWidth(container, page) {
  const wide = WIDE_PAGES.has(page);
  container.classList.toggle('max-w-6xl', !wide);
  container.classList.toggle('max-w-[1320px]', wide);
}

function render() {
  const page = getHash();
  const container = document.getElementById('page-content');

  // Handle vault detail routes: #vault/onyx, #vault/jade, etc.
  if (page.startsWith('vault/')) {
    const vaultKey = page.split('/')[1];
    _applyPageWidth(container, 'vault');
    container.innerHTML = renderVaultDetail(vaultKey);
    updateNav('vaults');
    requestAnimationFrame(() => initVaultDetail(vaultKey));
    return;
  }

  const route = routes[page];
  if (!route) { window.location.hash = '#overview'; return; }

  _applyPageWidth(container, page);
  container.innerHTML = route.render();
  updateNav(page);
  if (route.init) requestAnimationFrame(() => route.init());
}

// ── Network toggle UI ─────────────────────────────────────────────────
function updateNetworkUI() {
  const activeKey = getActiveNetworkKey();
  const dot = document.getElementById("network-dot");
  const label = document.getElementById("network-label");

  if (activeKey === "mainnet") {
    if (dot) dot.className = "w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot";
    if (label) label.textContent = "Mainnet";
  } else {
    if (dot) dot.className = "w-1.5 h-1.5 rounded-full bg-amber-400 pulse-dot";
    if (label) label.textContent = "Testnet";
  }
}

function wireNetworkMenu() {
  const btn = document.getElementById("network-btn");
  const menu = document.getElementById("network-menu");
  if (!btn || !menu) return;

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    menu.classList.toggle("hidden");
  });
  // Click outside to close
  document.addEventListener("click", (e) => {
    if (!btn.contains(e.target) && !menu.contains(e.target)) {
      menu.classList.add("hidden");
    }
  });

  document.querySelectorAll(".network-option").forEach(opt => {
    opt.addEventListener("click", () => {
      const target = opt.dataset.network;
      if (target && target !== getActiveNetworkKey()) {
        // Confirm switch — wallet state will reset and page reloads
        const targetLabel = NETWORKS[target]?.label || target;
        if (confirm(`Switch to ${targetLabel}? The page will reload.`)) {
          setActiveNetworkKey(target); // triggers page reload
        }
      }
      menu.classList.add("hidden");
    });
  });
}

// ── Wrong-chain banner ─────────────────────────────────────────────────
async function checkChainOnLoad() {
  if (!window.ethereum || !isConnected()) return;
  try {
    const chainHex = await window.ethereum.request({ method: "eth_chainId" });
    const chainId = parseInt(chainHex, 16);
    if (chainId !== NETWORK.chainId) {
      showWrongChainBanner(chainId);
    } else {
      hideWrongChainBanner();
    }
  } catch {}
}

function showWrongChainBanner(currentChainId) {
  const banner = document.getElementById("wrong-chain-banner");
  const nameEl = document.getElementById("banner-network-name");
  if (banner) banner.classList.remove("hidden");
  if (nameEl) nameEl.textContent = NETWORK.chainName;
}

function hideWrongChainBanner() {
  const banner = document.getElementById("wrong-chain-banner");
  if (banner) banner.classList.add("hidden");
}

// ── Wallet UI ─────────────────────────────────────────────────────────
function updateWalletUI() {
  const connectBtn = document.getElementById("connect-btn");
  const walletInfo = document.getElementById("wallet-info");

  if (isConnected()) {
    connectBtn.classList.add("hidden");
    walletInfo.classList.remove("hidden");
    walletInfo.classList.add("flex");
    document.getElementById("nav-wallet-label").textContent = shortAddr(getAddress());
    getUsdcBalance().then(bal => {
      document.getElementById("nav-balance").textContent = "$" + bal.toFixed(2);
    });
    // Keep explorer link in sync with current address + current network.
    const explorerLink = document.getElementById("wm-explorer");
    if (explorerLink) {
      const net = getActiveNetwork ? getActiveNetwork() : null;
      const base = net?.explorer || "https://www.hyperscan.com";
      explorerLink.href = `${base}/address/${getAddress()}`;
    }
  } else {
    connectBtn.classList.remove("hidden");
    walletInfo.classList.add("hidden");
    walletInfo.classList.remove("flex");
    closeWalletMenu();
  }
}

// Wallet dropdown: toggle on pill click, close on outside click or disconnect.
function openWalletMenu()  { document.getElementById("wallet-menu")?.classList.remove("hidden"); }
function closeWalletMenu() { document.getElementById("wallet-menu")?.classList.add("hidden"); }
function toggleWalletMenu() {
  const m = document.getElementById("wallet-menu");
  if (!m) return;
  m.classList.toggle("hidden");
}

function wireWalletMenu() {
  const pill = document.getElementById("wallet-info");
  if (!pill) return;
  pill.addEventListener("click", (e) => {
    e.stopPropagation();
    toggleWalletMenu();
  });
  // Close on click outside
  document.addEventListener("click", (e) => {
    const anchor = document.getElementById("wallet-menu-anchor");
    if (anchor && !anchor.contains(e.target)) closeWalletMenu();
  });
  // Close on Esc
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeWalletMenu(); });

  document.getElementById("wm-copy")?.addEventListener("click", async (e) => {
    e.stopPropagation();
    const addr = getAddress();
    if (!addr) return;
    try {
      await navigator.clipboard.writeText(addr);
      const lbl = document.getElementById("wm-copy-label");
      if (lbl) {
        const prev = lbl.textContent;
        lbl.textContent = "Copied!";
        setTimeout(() => { lbl.textContent = prev; }, 1200);
      }
    } catch { /* clipboard blocked; noop */ }
  });

  document.getElementById("wm-disconnect")?.addEventListener("click", (e) => {
    e.stopPropagation();
    disconnect();
    closeWalletMenu();
    updateWalletUI();
    render();
  });
}

// ── Init ──────────────────────────────────────────────────────────────
document.addEventListener("DOMContentLoaded", () => {
  initTxModal();
  updateNetworkUI();
  wireNetworkMenu();
  wireWalletMenu();
  wireHowItWorksTriggers();

  document.getElementById("connect-btn").addEventListener("click", async () => {
    await connect();
    updateWalletUI();
    checkChainOnLoad();
    render(); // re-render current page with wallet data
  });

  // "Switch network" button inside the wrong-chain banner triggers MetaMask switch
  document.getElementById("banner-switch-btn")?.addEventListener("click", async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: NETWORK.chainHex }],
      });
      hideWrongChainBanner();
    } catch (e) {
      if (e.code === 4902) {
        try {
          await window.ethereum.request({
            method: "wallet_addEthereumChain",
            params: [{
              chainId: NETWORK.chainHex,
              chainName: NETWORK.chainName,
              nativeCurrency: NETWORK.nativeCurrency,
              rpcUrls: [NETWORK.rpcUrl],
              blockExplorerUrls: [NETWORK.explorer],
            }],
          });
          hideWrongChainBanner();
        } catch {}
      }
    }
  });

  window.addEventListener("wallet-changed", () => {
    updateWalletUI();
    checkChainOnLoad();
    render();
  });

  window.addEventListener("wallet-wrong-chain", (e) => {
    showWrongChainBanner(e.detail?.chainId);
  });

  window.addEventListener("hashchange", render);

  // Always render the page first — wallet is optional for browsing
  render();

  // Try to silently reconnect (no MetaMask popup) if MetaMask still trusts us
  tryReconnect().then((addr) => {
    if (addr) {
      updateWalletUI();
      checkChainOnLoad();
      render(); // re-render to show wallet-aware data
    }
  });
});
